import React, { useState, useEffect, useRef } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import ColorThief from 'colorthief';

export const FooterComponent = ({ manager, clearFloatingNav = false }) => {
  const [dynamicGradient, setDynamicGradient] = useState(null);
  const imageRef = useRef(null);

  // Convert image filename to full URL using same logic as GuidesPage
  const getImageUrl = (imageFilename) => {
    if (!imageFilename) return null;
    
    // If it's already a full URL, return as is
    if (imageFilename.startsWith('http')) {
      return imageFilename;
    }
    
    // Use CloudFront URL like in GuidesPage
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net";
    return `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageFilename)}?width=600&format=webp`;
  };

  const imageUrl = getImageUrl(manager?.images);

  useEffect(() => {
    if (imageUrl && imageRef.current) {
      const img = imageRef.current;
      
      if (img.complete) {
        extractMainColor(img);
      } else {
        img.onload = () => extractMainColor(img);
      }
    }
  }, [imageUrl]);

  const extractMainColor = (img) => {
    try {
      const colorThief = new ColorThief();
      const dominantColor = colorThief.getColor(img);
      
      if (dominantColor && dominantColor.length === 3) {
        let [r, g, b] = dominantColor;
        
        // Calculate brightness (0-255 scale)
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        
        // If the color is too dark (brightness < 100), add brightness
        if (brightness < 100) {
          const brightnessBoost = Math.min(100, 150 - brightness); // Boost up to 150 brightness
          r = Math.min(255, r + brightnessBoost);
          g = Math.min(255, g + brightnessBoost);
          b = Math.min(255, b + brightnessBoost);
        }
        
        const gradient = `linear-gradient(180deg, rgba(26, 32, 44, 0.8) 0%, rgba(${r}, ${g}, ${b}, 0.4) 100%)`;
        setDynamicGradient(gradient);
      }
    } catch (error) {
      console.log('Color extraction failed, using default gradient:', error.message);
      // Fallback to default gradient
      setDynamicGradient('linear-gradient(180deg, rgba(26, 32, 44, 0.8) 0%, rgba(129, 126, 255, 0.3) 50%, rgba(97, 94, 220, 0.4) 100%)');
    }
  };

  return (
    <Box
      bg={dynamicGradient || "linear-gradient(180deg, rgba(26, 32, 44, 0.8) 0%, rgba(129, 126, 255, 0.3) 50%, rgba(97, 94, 220, 0.4) 100%)"}
      p={4}
      // Extra room at the very bottom so the floating nav never sits on the
      // logo (bar is ~58px tall plus its 16px offset).
      pb={clearFloatingNav ? "calc(88px + env(safe-area-inset-bottom))" : 4}
      mt={4}
      textAlign="center"
      backdropFilter="blur(10px)"
    >
      <Flex direction="column" alignItems="center" gap={2}>
        {imageUrl && (
          <Image
            ref={imageRef}
            src={imageUrl}
            alt={manager?.name || "Company Logo"}
            w="50px"
            h="50px"
            objectFit="cover"
            borderRadius="8px"
            border="2px solid rgba(129, 126, 255, 0.18)"
            boxShadow="0 3px 10px rgba(129, 126, 255, 0.15)"
            bg="rgba(26, 32, 44, 0.8)"
            crossOrigin="anonymous"
          />
        )}
        <Text
          fontSize="12px"
          fontWeight="600"
          color="rgba(255, 255, 255, 0.9)"
          textShadow="0 1px 2px rgba(0, 0, 0, 0.3)"
        >
          {manager?.name || "Linkbase"}
        </Text>
      </Flex>
    </Box>
  );
}; 